import { useEffect, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useChartTheme } from '@/components/base/chartTheme';

interface Pt {
  time: string; soc: number | null; power: number | null;
  status: string | null; flow: string | null; dcV: number | null; hz: number | null; soh: number | null;
}

// Dark surface (this page is always dark, like /forecast)
const cardBg = '#1A1A1A';
const cardBorder = 'rgba(255,255,255,0.07)';
const textMain = '#E0E0E0';
const textMuted = '#6B6B70';
const textDim = '#4A4A50';
const innerBg = '#0F0F0F';
const rowBorder = 'rgba(255,255,255,0.05)';
const headBg = 'rgba(255,255,255,0.03)';
// Кольори серій — зі стандарту (chartTheme): SoC --ok-2, потужність --ok.


const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const num = (n: number | null | undefined, d = 0) =>
  n == null ? '—' : Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

const statusStyle = (rs: string | null) => {
  const s = (rs || '').toLowerCase();
  if (s === 'run' || s === 'running') return { t: 'Робота', c: '#22C55E' };
  if (s === 'fault') return { t: 'Аварія', c: 'var(--accent)' };
  if (s === 'stop' || s === 'standby' || s === 'idle') return { t: 'Стоп', c: '#6B6B70' };
  return { t: rs || '—', c: textMuted };
};
const flowStyle = (pf: string | null, power: number | null) => {
  const f = (pf || '').toLowerCase();
  if (f.includes('charg') || (power ?? 0) < -0.3) return { t: 'Заряд', c: '#1F5B8F' };
  if (f.includes('dischar') || (power ?? 0) > 0.3) return { t: 'Розряд', c: '#E0A030' };
  return { t: 'Спокій', c: '#6B6B70' };
};

export default function BessSocPowerChart({ assetId }: { assetId: string }) {
  const ct = useChartTheme();
  const [date, setDate] = useState(todayStr());
  const [pts, setPts] = useState<Pt[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    authFetch(`${API_BASE_URL}/forecasting/assets/${assetId}/bess/day?date=${date}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const points: Pt[] = (d.points || []).map((p: any) => ({
          time: p.time,
          soc: p.battery?.socPct ?? null,
          power: p.pcs?.outputActivePowerKw ?? null,
          status: p.status?.runningStatus ?? null,
          flow: p.status?.powerFlow ?? null,
          dcV: p.pcs?.dcVoltageV ?? null,
          hz: p.pcs?.gridFrequencyHz ?? null,
          soh: p.battery?.sohPct ?? null,
        }));
        setPts(points);
      })
      .catch(() => setPts([]))
      .finally(() => setLoading(false));
  }, [assetId, date]);

  const th = 'text-left text-[10px] font-bold uppercase tracking-wider px-2 py-1.5';
  const td = 'px-2 py-1.5 text-[12px] tabular-nums whitespace-nowrap';

  return (
    <>
      {/* SoC + power chart */}
      <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: textMain }}>Історія: SoC та потужність</div>
            <div className="text-[10px] mt-0.5" style={{ color: textMuted }}>заряд (SoC, %) і потужність PCS (кВт; − заряджання / + розряджання) за добу</div>
          </div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md outline-none cursor-pointer"
            style={{ background: innerBg, border: `1px solid ${cardBorder}`, color: textMain, colorScheme: 'dark' }} />
        </div>
        <div className="flex items-center gap-4 mb-2 text-[11px]" style={{ color: textMuted }}>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ct.series.ok2 }} /> SoC, %</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ct.series.ok }} /> Потужність, кВт</span>
        </div>
        <div style={{ height: 260 }}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-xs" style={{ color: textMuted }}>Завантаження…</div>
          ) : !pts || pts.length === 0 ? (
            <div className="flex items-center justify-center h-full text-xs" style={{ color: textMuted }}>Немає даних BESS за {date}</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pts} margin={{ top: 8, right: 6, left: -10, bottom: 0 }}>
                <CartesianGrid {...ct.gridProps} />
                <XAxis dataKey="time" {...ct.axis}
                  interval={Math.max(0, Math.floor(pts.length / 12))} />
                <YAxis yAxisId="soc" domain={[0, 100]} {...ct.axis} width={34}
                  tickFormatter={(v: number) => `${v}%`} />
                <YAxis yAxisId="pwr" orientation="right" {...ct.axis} width={42} />
                <Tooltip
                  {...ct.tooltip} cursor={ct.cursorLine}
                  formatter={(v: number, n: string) => (n === 'soc' ? [`${v?.toFixed?.(1) ?? v} %`, 'SoC'] : [`${v?.toFixed?.(1) ?? v} кВт`, 'Потужність'])}
                  labelFormatter={(l: string) => `${l}`} />
                <ReferenceLine yAxisId="pwr" y={0} stroke={ct.dim} strokeDasharray="2 2" />
                <Line yAxisId="pwr" dataKey="power" name="power" stroke={ct.series.ok} {...ct.line} />
                <Line yAxisId="soc" dataKey="soc" name="soc" stroke={ct.series.ok2} {...ct.line} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Status / readings table */}
      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${cardBorder}` }}>
          <div className="text-sm font-semibold" style={{ color: textMain }}>Журнал станів BESS</div>
          <div className="text-[10px]" style={{ color: textDim }}>читання за {date} · {pts?.length ?? 0} записів</div>
        </div>
        <div className="overflow-auto" style={{ maxHeight: 360 }}>
          {(!pts || pts.length === 0) ? (
            <div className="text-xs px-4 py-4" style={{ color: textMuted }}>{loading ? 'Завантаження…' : `Немає записів за ${date}`}</div>
          ) : (
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead className="sticky top-0" style={{ background: headBg }}>
                <tr style={{ color: textMuted }}>
                  <th className={th}>Час</th>
                  <th className={th}>Статус</th>
                  <th className={th}>Режим</th>
                  <th className={th} style={{ textAlign: 'right' }}>SoC, %</th>
                  <th className={th} style={{ textAlign: 'right' }}>Потужність, кВт</th>
                  <th className={th} style={{ textAlign: 'right' }}>DC, В</th>
                  <th className={th} style={{ textAlign: 'right' }}>Частота, Гц</th>
                </tr>
              </thead>
              <tbody>
                {[...pts].reverse().map((p, i) => {
                  const st = statusStyle(p.status);
                  const fl = flowStyle(p.flow, p.power);
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${rowBorder}`, color: textMain }}>
                      <td className={td}>{p.time}</td>
                      <td className={td}><span style={{ color: st.c }}>● {st.t}</span></td>
                      <td className={td}><span style={{ color: fl.c }}>{fl.t}</span></td>
                      <td className={td} style={{ textAlign: 'right' }}>{num(p.soc, 1)}</td>
                      <td className={td} style={{ textAlign: 'right', color: (p.power ?? 0) < 0 ? '#1F5B8F' : (p.power ?? 0) > 0 ? '#E0A030' : textMuted }}>{num(p.power, 1)}</td>
                      <td className={td} style={{ textAlign: 'right', color: textMuted }}>{num(p.dcV, 0)}</td>
                      <td className={td} style={{ textAlign: 'right', color: textMuted }}>{num(p.hz, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
